import { Music24LicenseStateType } from 'fym-common/src/license/music24Types';
import { trialDaysLeft } from '../modules/music24/watermarkUpsell/trial/trialDaysLeft';

export class LicenseInfo {
  isValid: boolean;

  license: Music24LicenseStateType | null;

  validLicense: Music24LicenseStateType | null;

  constructor(license: Music24LicenseStateType | null) {
    this.license = license;
    this.validLicense = license && license.isValid ? license : null;
    this.isValid = !!license && license.isValid;
  }

  get subscriptionExpiresAt() {
    return this.validLicense && this.validLicense.subscriptionExpiresAt !== null
      ? new Date(this.validLicense.subscriptionExpiresAt)
      : undefined;
  }

  /**
   * True while the licence has a live paid plan or a live trial. The server builds the plan from
   * the live items only, and its expiry roll-up skips an item with no end date (an admin grant).
   * So a paid plan with no date, or with a past date, comes from a never-expiring item and counts
   * as active (music24-api `resolveMusic24CapabilitiesForLicense` has the same rule).
   */
  isSubscriptionValid(): boolean {
    if (!this.isValid) {
      return false;
    }
    const expiresAt = this.subscriptionExpiresAt;
    if (expiresAt !== undefined && expiresAt.getTime() > Date.now()) {
      return true;
    }
    const plan = this.validLicense?.smlCapabilities?.plan;
    return plan !== undefined && plan !== 'free' && plan !== 'trial';
  }

  get trialStatus(): 'active' | 'expired' | 'none' {
    return this.license?.trial?.status ?? 'none';
  }

  get trialEndsAt(): Date | null {
    const endsAt = this.license?.trial?.endsAt;
    return endsAt !== undefined && endsAt !== null ? new Date(endsAt) : null;
  }

  /**
   * Set while a plan bought during the signup trial (Artist) keeps the trial limits. The limits drop
   * to the plan's own at this time.
   */
  get trialLimitsUntil(): Date | null {
    const until = this.license?.trialLimitsUntil;
    return until !== undefined && until !== null ? new Date(until) : null;
  }

  get isTrialActive(): boolean {
    return this.trialStatus === 'active';
  }

  get trialDaysRemaining(): number | null {
    if (this.trialStatus !== 'active' || !this.trialEndsAt) {
      return null;
    }
    // One rule for every place that shows the days left (QA finding F08).
    return trialDaysLeft(this.trialEndsAt);
  }
}
